import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';

const TERM_OPTIONS = ['1학기', '2학기'];
const TYPECLS_OPTIONS = ['정규', '특별', '보충', '방과후'];
const CLASSTYPE_OPTIONS = [
  { value: 'R', label: '정규반' },
  { value: 'M', label: '면장반' },
];

// 학년 -> 학기 순으로 연속된 행을 묶어서, 표에서 학년/학기 칸을 세로로 병합(rowSpan)해 보여주기 위한 행 목록을 만든다.
// rows는 이미 grade, startdate 순으로 정렬되어 내려오므로 같은 학년/학기는 항상 연속으로 붙어 있다.
function buildGroupedRows(rows) {
  const result = [];
  let i = 0;
  while (i < rows.length) {
    const grade = rows[i].grade;
    let gradeEnd = i;
    while (gradeEnd < rows.length && rows[gradeEnd].grade === grade) gradeEnd++;
    const gradeRowSpan = gradeEnd - i;
    let gradeFirst = true;

    let j = i;
    while (j < gradeEnd) {
      const term = rows[j].term;
      let termEnd = j;
      while (termEnd < gradeEnd && rows[termEnd].term === term) termEnd++;
      const termRowSpan = termEnd - j;
      let termFirst = true;

      for (let k = j; k < termEnd; k++) {
        result.push({
          row: rows[k],
          showGrade: gradeFirst,
          gradeRowSpan,
          showTerm: termFirst,
          termRowSpan,
        });
        gradeFirst = false;
        termFirst = false;
      }
      j = termEnd;
    }
    i = gradeEnd;
  }
  return result;
}

function emptyForm() {
  return {
    term: '1학기',
    typecls: '정규',
    grade: 1,
    clstime: 4,
    classtype: 'R',
    startdate: '',
    enddate: '',
    remark: '',
  };
}

export default function CoursePlanning() {
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [message, setMessage] = useState('');

  const loadRows = useCallback(async () => {
    const res = await api.get('/admin/course-plans');
    setRows(res.data);
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormOpen(true);
    setMessage('');
  };

  const openEdit = (row) => {
    setEditingId(row.cpid);
    setForm({
      term: row.term,
      typecls: row.typecls,
      grade: row.grade,
      clstime: row.clstime,
      classtype: row.classtype,
      startdate: row.startdate,
      enddate: row.enddate,
      remark: row.remark || '',
    });
    setFormOpen(true);
    setMessage('');
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.startdate || !form.enddate) {
      setMessage('수업기간(시작/종료)을 입력하세요.');
      return;
    }
    const body = {
      ...form,
      grade: Number(form.grade),
      clstime: Number(form.clstime),
      remark: form.remark.trim() || null,
    };
    try {
      if (editingId) {
        await api.put(`/admin/course-plans/${editingId}`, body);
      } else {
        await api.post('/admin/course-plans', body);
      }
      closeForm();
      await loadRows();
    } catch (err) {
      setMessage(err.response?.data?.detail || '저장에 실패했습니다.');
    }
  };

  const deleteRow = async (cpid) => {
    if (!window.confirm('이 교육과정 기획을 삭제하시겠습니까?')) return;
    await api.delete(`/admin/course-plans/${cpid}`);
    await loadRows();
  };

  const classtypeLabel = (v) => CLASSTYPE_OPTIONS.find((c) => c.value === v)?.label || v;
  const groupedRows = buildGroupedRows(rows);

  return (
    <div className="page">
      <div className="page-header">
        <h2>교육과정 기획</h2>
      </div>

      <div className="card">
        <div className="board-header">
          <h3>연간 교육과정 운영 계획</h3>
          {!formOpen && (
            <button className="btn-primary btn-inline" onClick={openCreate}>
              + 새 계획 추가
            </button>
          )}
        </div>

        {formOpen && (
          <form className="board-write-form" onSubmit={submitForm}>
            <div className="form-row">
              <div className="form-field form-field-narrow">
                <label>학기</label>
                <select value={form.term} onChange={(e) => updateField('term', e.target.value)}>
                  {TERM_OPTIONS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="form-field form-field-narrow">
                <label>수업종류</label>
                <select value={form.typecls} onChange={(e) => updateField('typecls', e.target.value)}>
                  {TYPECLS_OPTIONS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="form-field form-field-narrow">
                <label>대상학년</label>
                <select value={form.grade} onChange={(e) => updateField('grade', e.target.value)}>
                  <option value={1}>1학년</option>
                  <option value={2}>2학년</option>
                  <option value={3}>3학년</option>
                </select>
              </div>
              <div className="form-field form-field-narrow">
                <label>구분</label>
                <select value={form.classtype} onChange={(e) => updateField('classtype', e.target.value)}>
                  {CLASSTYPE_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-field form-field-narrow">
                <label>일일 수업시간</label>
                <input
                  type="number"
                  min="1"
                  value={form.clstime}
                  onChange={(e) => updateField('clstime', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field form-field-narrow">
                <label>시작일</label>
                <input
                  type="date"
                  value={form.startdate}
                  onChange={(e) => updateField('startdate', e.target.value)}
                />
              </div>
              <div className="form-field form-field-narrow">
                <label>종료일</label>
                <input
                  type="date"
                  value={form.enddate}
                  onChange={(e) => updateField('enddate', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>비고</label>
                <input
                  className="field-input"
                  type="text"
                  value={form.remark}
                  onChange={(e) => updateField('remark', e.target.value)}
                />
              </div>
            </div>

            {message && <div className="form-message">{message}</div>}

            <div className="comment-actions">
              <button className="btn-primary btn-inline" type="submit">
                {editingId ? '수정 저장' : '등록'}
              </button>
              <button className="btn-ghost" type="button" onClick={closeForm}>취소</button>
            </div>
          </form>
        )}

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>학년</th>
                <th>학기</th>
                <th>수업종류</th>
                <th>구분</th>
                <th>일일 수업시간</th>
                <th>수업기간</th>
                <th>비고</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {groupedRows.map(({ row: r, showGrade, gradeRowSpan, showTerm, termRowSpan }) => (
                <tr key={r.cpid}>
                  {showGrade && <td rowSpan={gradeRowSpan}>{r.grade}학년</td>}
                  {showTerm && <td rowSpan={termRowSpan}>{r.term}</td>}
                  <td>{r.typecls}</td>
                  <td>{classtypeLabel(r.classtype)}</td>
                  <td>{r.clstime}시간</td>
                  <td>{r.startdate} ~ {r.enddate}</td>
                  <td>{r.remark || ''}</td>
                  <td className="board-title-cell">
                    <button className="btn-link" onClick={() => openEdit(r)}>수정</button>
                    {' '}
                    <button className="btn-link" onClick={() => deleteRow(r.cpid)}>삭제</button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan="8" className="empty-row">등록된 교육과정 기획이 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
